/**
 * Экран «Заявка команды» (задача 43) — заявка **одной** команды глазами организатора.
 *
 * Открывается из раздела «Организатор» (`#/organizer`): название команды в любой из четырёх
 * таблиц — гиперссылка с адресом команды (`#/organizer/team?ownerUserId=…`,
 * `src/frontend/router.ts`), поэтому заявку можно открыть заново, обновить страницу (F5) и
 * переслать. Данные берутся одним запросом `GET /api/organizer/team` (задача 43): состав
 * команды читает сервер — заявка каждого тренера закрыта его сессией.
 *
 * Секции повторяют экран заявки тренера (`src/frontend/application.ts`) и собраны теми же
 * примитивами: сворачиваемые секции (`sectionFrame`), таблицы `.table` с `cell` (на узком
 * экране — карточки, на широком — таблица), список «подпись — значение» (`detailsList`) в
 * «Организации». Отличие одно: **экран только читает**. Ни «карандаша», ни «галочки»,
 * «крестика» и «+» здесь нет; столбца «Действия» у таблиц тоже нет. Вместо блока
 * подтверждения — строка `TEAM_CONFIRMED_NOTE` (в списках раздела всегда лежат только
 * подтверждённые заявки).
 *
 * Права проверяет API (`withRoles`: роль `organizer`), а адрес экрана показывается только
 * организатору (`main.ts`) — как и весь раздел.
 */
import {
  GENDER_LABELS,
  disciplineName,
  documentTitle,
  stationName,
} from '../shared/dictionaries';
import type {
  OrganizerTeamAthlete,
  OrganizerTeamDocument,
  OrganizerTeamDuilian,
  OrganizerTeamView,
  PublicUser,
  Transfer,
} from '../shared/types';
import { userMasthead } from './accountMenu';
import { ApiError, fetchOrganizerTeam } from './api';
import { el, link, messageBox, renderScreen, type MessageBox } from './dom';
import { ORGANIZER_HREF } from './router';
import {
  TEAM_CONFIRMED_NOTE,
  cell,
  cellList,
  detailsList,
  emptyNote,
  readonlyTable,
  sectionFrame,
  showLoadError,
  valueCell,
} from './ui';

/** Подписи колонок таблицы «Спортсмены» — те же, что на экране заявки тренера. */
const ATHLETE_COLUMNS = [
  'ФИО',
  'Дата рождения',
  'Пол',
  'Размер футболки',
  'Виды программы',
  'Документы',
];

/** Подписи колонок секции «Дуйлянь»: вид выступления и его спортсмены. */
const DUILIAN_COLUMNS = ['Тип выступления', 'Спортсмены'];

/** Подписи колонок таблицы «Представители» — те же, что на экране заявки тренера. */
const REPRESENTATIVE_COLUMNS = ['ФИО', 'Размер футболки'];

/**
 * Колонки строки приезда/отъезда: тот же порядок, что у формы заявки (дата, время,
 * количество человек, вокзал, «Трансфер нужен», номер поезда или рейса). Колонка
 * «Раннее заселение» — свойство строки приезда, поэтому добавляется отдельно.
 */
const TRANSFER_COLUMNS = [
  'Дата',
  'Время',
  'Количество человек',
  'Вокзал / аэропорт',
  'Трансфер нужен',
  'Номер поезда / рейса',
];
const EARLY_CHECK_IN_COLUMN = 'Раннее заселение';

/** Текст загрузки: состав читается запросом, пока ответа нет — показывать нечего. */
const LOADING = 'Загружаем заявку команды…';

/** Подписи пустых секций: пустая заявка — состояние экрана, а не ошибка. */
const NO_ATHLETES = 'Спортсмены не заявлены.';
const NO_DUILIAN = 'Выступлений «Дуйлянь» нет.';
const NO_REPRESENTATIVES = 'Представители не заявлены.';
const NO_TRANSFERS = 'Строк нет.';

/** Что видно в списке документов спортсмена: файл есть или его ещё нет (мандатная комиссия). */
const DOCUMENT_UPLOADED = 'загружен';
const DOCUMENT_MISSING = 'не загружен';

/**
 * Открывает экран «Заявка команды»: панель «Назад», сообщение и секции заявки.
 * Сразу показывает сообщение о загрузке — состав приходит ответом, заранее его нет.
 */
export function showOrganizerTeamScreen(ownerUserId: string, user: PublicUser): void {
  const message = messageBox();
  const sections = el('div', { className: 'application' });
  renderScreen(
    'Заявка команды',
    el('div', {
      className: 'application-screen team-screen',
      children: [message.root, backPanel(), sections],
    }),
    userMasthead(user),
  );

  message.show(LOADING, 'info');

  fetchOrganizerTeam(ownerUserId)
    .then((team) => {
      message.clear();
      sections.replaceChildren(...teamSections(team));
    })
    .catch((err: unknown) => showTeamError(err, message));
}

/**
 * Панель с кнопкой «Назад». Это **ссылка** на раздел, а не `history.back()`: экран
 * открывается и по прямой ссылке, и после перезагрузки страницы, когда «назад» в истории
 * браузера ведёт не в раздел. Раздел при возврате рисуется из памяти браузера
 * (`organizerCache.ts`, задача 35) — то есть мгновенно. При прокрутке панель залипает
 * (стили `.team-back`): другой навигации у экрана нет, и длинная заявка иначе «запирала»
 * бы организатора внизу страницы.
 */
function backPanel(): HTMLElement {
  return el('div', { className: 'team-back', children: [link('Назад', ORGANIZER_HREF)] });
}

/**
 * Ошибка загрузки. `404` — команду сняли с подтверждения (или владельца не подтвердил
 * администратор), пока раздел был открыт: это состояние экрана, а не сбой, поэтому
 * показываем текст сервера, а «Назад» остаётся на месте. Остальное разбирает общий код:
 * истёкшая сессия уводит на экран входа, сбой хранилища показывается сообщением.
 */
function showTeamError(err: unknown, message: MessageBox): void {
  if (err instanceof ApiError && err.status === 404) {
    message.show(err.message, 'error');
    return;
  }
  showLoadError(err, message);
}

/** Секции экрана в том же порядке, что у заявки тренера, плюс заметка о подтверждении. */
function teamSections(team: OrganizerTeamView): HTMLElement[] {
  return [
    organizationSection(team),
    athletesSection(team.athletes),
    duilianSection(team.duilian),
    representativesSection(team),
    transfersSection(team),
    confirmedNote(),
  ];
}

/** Секция экрана: тот же сворачиваемый каркас, что у секций заявки. */
function section(title: string, content: Node): HTMLElement {
  const body = el('div', { className: 'section__body', children: [content] });
  return sectionFrame(title, body).root;
}

/**
 * Секция «Организация»: те же поля, что у тренера, — только чтением, поэтому список
 * «подпись — значение» (`detailsList`), а не форма с «карандашом» и автосохранением.
 */
function organizationSection(team: OrganizerTeamView): HTMLElement {
  const { organization } = team;
  const list = el('div', {
    className: 'row-view',
    children: [
      detailsList([
        { label: 'Название организации', value: organization.name },
        { label: 'Имя руководителя', value: organization.headName },
        { label: 'Email', value: organization.email },
        { label: 'Телефон', value: organization.phone },
        { label: 'Город', value: organization.city },
        { label: 'Страна', value: organization.country },
      ]),
    ],
  });
  return section('Организация', list);
}

/**
 * Секция «Спортсмены»: вместо чисел — **списки**. Виды программы показываются построчно
 * (каждая строка — отдельное выступление: спортсмен выходит на вид повторно, а в зачёт
 * идёт лучшая оценка), документы — по типу из словаря с признаком «загружен / не загружен».
 */
function athletesSection(athletes: OrganizerTeamAthlete[]): HTMLElement {
  const content =
    athletes.length === 0
      ? emptyNote(NO_ATHLETES)
      : readonlyTable({
          columns: ATHLETE_COLUMNS,
          rows: athletes.map((athlete) => [
            cell(ATHLETE_COLUMNS[0], valueCell(athlete.fullName)),
            cell(ATHLETE_COLUMNS[1], valueCell(athlete.birthDate)),
            cell(ATHLETE_COLUMNS[2], valueCell(GENDER_LABELS[athlete.gender])),
            cell(ATHLETE_COLUMNS[3], valueCell(athlete.tshirtSize)),
            cell(
              ATHLETE_COLUMNS[4],
              cellList(athlete.disciplines.map((disciplineId) => disciplineName(disciplineId))),
            ),
            cell(ATHLETE_COLUMNS[5], cellList(athlete.documents.map(documentLine))),
          ]),
        });
  return section(`Спортсмены: ${athletes.length}`, content);
}

/** Строка списка документов: название типа из словаря и признак загрузки файла. */
function documentLine(document: OrganizerTeamDocument): string {
  const state = document.uploaded ? DOCUMENT_UPLOADED : DOCUMENT_MISSING;
  return `${documentTitle(document.docId)} — ${state}`;
}

/** Секция «Дуйлянь»: вид выступления и его спортсмены (2–3) — как в заявке. */
function duilianSection(entries: OrganizerTeamDuilian[]): HTMLElement {
  const content =
    entries.length === 0
      ? emptyNote(NO_DUILIAN)
      : readonlyTable({
          columns: DUILIAN_COLUMNS,
          rows: entries.map((entry) => [
            cell(DUILIAN_COLUMNS[0], valueCell(disciplineName(entry.disciplineId))),
            cell(DUILIAN_COLUMNS[1], cellList(entry.athletes)),
          ]),
        });
  return section(`Дуйлянь: ${entries.length}`, content);
}

/**
 * Секция «Представители»: строки и подпись «Количество остальных сопровождающих» под
 * таблицей — это поле секции целиком, а не строка (друзья и родители едут без ФИО).
 */
function representativesSection(team: OrganizerTeamView): HTMLElement {
  const content =
    team.representatives.length === 0
      ? emptyNote(NO_REPRESENTATIVES)
      : readonlyTable({
          columns: REPRESENTATIVE_COLUMNS,
          rows: team.representatives.map((representative) => [
            cell(REPRESENTATIVE_COLUMNS[0], valueCell(representative.fullName)),
            cell(REPRESENTATIVE_COLUMNS[1], valueCell(representative.tshirtSize)),
          ]),
        });
  const accompanying = el('p', {
    className: 'section__hint',
    text: `Количество остальных сопровождающих: ${team.accompanyingCount}`,
  });
  const body = el('div', { children: [content, accompanying] });
  return section(`Представители: ${team.representatives.length}`, body);
}

/**
 * Секция «Гостиница и трансфер от организаторов»: подсекции «Приезд» (с колонкой
 * «Раннее заселение») и «Отъезд» — колонки те же, что на экране заявки. Названия вокзалов
 * берутся из справочника (`stationName`) — как в таблицах раздела.
 */
function transfersSection(team: OrganizerTeamView): HTMLElement {
  const subsections = el('div', {
    className: 'section__body',
    children: [
      transferSubsection('Приезд', team.arrivals, true),
      transferSubsection('Отъезд', team.departures, false),
    ],
  });
  return sectionFrame('Гостиница и трансфер от организаторов', subsections).root;
}

/** Подсекция «Приезд»/«Отъезд»: таблица строк команды; «Раннее заселение» — у приезда. */
function transferSubsection(
  title: string,
  rows: Transfer[],
  withEarlyCheckIn: boolean,
): HTMLElement {
  const columns = withEarlyCheckIn
    ? [...TRANSFER_COLUMNS, EARLY_CHECK_IN_COLUMN]
    : TRANSFER_COLUMNS;
  const body = el('div', { className: 'section__body' });
  const subsection = sectionFrame(title, body, { subsection: true }).root;

  body.append(
    rows.length === 0
      ? emptyNote(NO_TRANSFERS)
      : readonlyTable({
          columns,
          rows: rows.map((row) => {
            const cells = [
              cell(columns[0], valueCell(row.date)),
              cell(columns[1], valueCell(row.time)),
              cell(columns[2], valueCell(row.peopleCount === 0 ? '' : row.peopleCount)),
              cell(columns[3], valueCell(stationName(row.stationId))),
              cell(columns[4], valueCell(row.transferNeeded ? 'Да' : 'Нет')),
              cell(columns[5], valueCell(row.trainOrFlightNumber)),
            ];
            if (withEarlyCheckIn) {
              cells.push(cell(columns[6], valueCell(row.earlyCheckIn ? 'Да' : 'Нет')));
            }
            return cells;
          }),
        }),
  );
  return subsection;
}

/**
 * Заметка вместо блока подтверждения: в списках раздела всегда лежат только подтверждённые
 * заявки, поэтому чекбокса и кнопки здесь нет — состояние заявки читается одной строкой.
 */
function confirmedNote(): HTMLElement {
  return el('div', {
    className: 'confirm team-confirm',
    children: [el('p', { className: 'confirm__published', text: TEAM_CONFIRMED_NOTE })],
  });
}
